import { CombinedGraphQLErrors } from '@apollo/client';
import { getFirstNestJsGraphQLError } from '@pcg/graphql-kit';

import { useErrorStore } from '@/shared/stores/error';
import { useAuthStore } from '@/stores/auth';

import type { RouteLocationNormalized } from 'vue-router';

const isPublicPage = (route: RouteLocationNormalized): boolean => Boolean(route.meta.public);

/**
 * `fullPath`, not `path`: the query and hash are part of where the user was
 * going. `/declensions/table?pattern=zena` sent through `path` alone drops the
 * user on the bare table after sign-in, having silently thrown away the thing
 * they clicked. It stays a same-origin relative path, which is what
 * `safeRedirectPath` on the way back requires.
 */
const toLogin = (to: RouteLocationNormalized) => ({
  name: 'Login',
  query: { redirect_uri: to.fullPath },
});

/**
 * Cookie-based auth guard.
 *
 * The session cookie is HttpOnly, so there is nothing to inspect from JS — no
 * token presence, no expiry. The guard therefore does not decide, it ASKS: it
 * calls `me` and reads the answer. Every route is private unless it declares
 * `meta.public`.
 */
export const authMiddleware = async (to: RouteLocationNormalized) => {
  const auth = useAuthStore();

  // === PUBLIC PAGES ===
  if (isPublicPage(to)) {
    if (!auth.user) {
      try {
        await auth.fetchMe();
      } catch (e) {
        // A failure here is not fatal: the page is public. Fall through and
        // render it as an anonymous visitor.
        console.error('[middlewares/auth] Failed to fetch the user on a public page:', e);
      }
    }

    // An authenticated user has no business on the login screen.
    if (auth.user && !auth.isGuest && to.name === 'Login') {
      return { name: 'Home' };
    }

    return;
  }

  // === FETCH USER IF NEEDED ===
  if (!auth.user) {
    try {
      await auth.fetchMe();
    } catch (e) {
      console.error('[middlewares/auth] Failed to fetch the user:', e);

      // Anything that is not a GraphQL response never reached the resolver:
      // the API is down, DNS or TLS failed, CORS rejected the request. None of
      // those is a sign-in problem, and /login is a form that cannot fix them —
      // it would just fail the same way on submit. Same treatment as a 500.
      if (!CombinedGraphQLErrors.is(e)) {
        useErrorStore().setError({
          statusCode: 500,
          title: 'Сервер не отвечает',
          description: 'Не удалось связаться с сервером. Проверь соединение и попробуй ещё раз.',
          critical: true,
        });

        return;
      }

      const nestJsError = getFirstNestJsGraphQLError(e);
      const statusCode = nestJsError?.extensions?.statusCode;

      // 403, 500 and "no status at all" are not sign-in problems either —
      // sending the user to /login would only loop them through a form that
      // cannot help. Show the error where they are. Only a real 401 falls
      // through to the redirect below.
      if (statusCode === 403 || statusCode === 500 || !statusCode) {
        useErrorStore().setError({
          statusCode: statusCode ?? 500,
          title: statusCode === 403 ? 'Нет доступа' : 'Ошибка сервера',
          description: nestJsError?.message ?? 'Не удалось связаться с сервером. Попробуй позже.',
          critical: true,
        });

        return;
      }

      return toLogin(to);
    }
  }

  // === CHECK AUTH ===
  if ((!auth.user || auth.isGuest) && to.name !== 'Login') {
    return toLogin(to);
  }
};
